import { FC, forwardRef, MouseEvent, useCallback, useContext, useMemo, } from 'react';
import { Link, useSearchParams } from 'react-router';

import { getQuery } from '@utils/navigation.ts';
import { resolveExternalNavigationDestination } from '@utils/androidBrowserIntentUrl.ts';
import { IFotoscapeLinkProps } from './interfaces';
import { getIsLinkExternal } from './utils';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useContentDismissal } from '@moment/analytics/ContentDismissalContext.tsx';

// Safe hook that returns null if AnalyticsProvider is not available
const useSafeAnalytics = () => {
  try {
    return useAnalytics();
  } catch {
    // AnalyticsProvider not available, return null
    return null;
  }
};

const FotoscapeLink: FC<IFotoscapeLinkProps> = forwardRef((props, ref) => {
  const [search] = useSearchParams();
  const analytics = useSafeAnalytics();
  const contentDismissalService = useContentDismissal();
  const context = useContext(CommonPageDataContext);

  const mergedTo = useMemo(() => {
    if (typeof props.to === 'string') {
      const origin = props.to.split('?')[0];
      const searchString = props.to.split('?')[1];

      return `${origin}?${getQuery(searchString, search)}`;
    }

    return {
      ...props.to,
      search: getQuery(props.to.search || '', search),
    };
  }, [props.to, search]);

  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const { target } = e.currentTarget;

      if (props.onClick) {
        props.onClick(e);
      }

      const nextPath =
        typeof mergedTo === 'string' ? mergedTo : mergedTo?.pathname || '';

      let eventCallback = undefined;
      let eventTimeout = undefined;

      const isAndroid = /Android/i.test(navigator.userAgent);

      if (getIsLinkExternal(nextPath)) {
        contentDismissalService?.stopTracking('external_navigation');
        if (target !== '_blank' || isAndroid) {
          const navigateTo =
            typeof mergedTo === 'string'
              ? resolveExternalNavigationDestination(mergedTo)
              : nextPath;
          // pause to allow event to be recorded before redirecting user
          e.preventDefault();
          eventCallback = () => {
            window.location.href = isAndroid ? navigateTo : mergedTo;
          };
          eventTimeout = 300;
        }
      }

      // Only emit analytics event if both analytics and context are available
      if (analytics && context) {
        analytics.emitLinkClickedEvent({
          partnerId: context.partner || '',
          originUrl: window.location.href,
          destinationUrl: nextPath,
          searchParams: search,
          eventCallback: eventCallback,
          eventTimeout: eventTimeout,
        });
      }
    },
    [mergedTo, props, contentDismissalService, analytics, context, search]
  );

  return <Link ref={ref} {...props} to={mergedTo} onClick={onClick} state={{ ...props.state, fromApp: true }} />;
});

export default FotoscapeLink;
